import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

export const getCustomers = createAsyncThunk(
  "Customer/get",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== ""
        )
      );
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.CUSTOMER.SLUG}?${queryString}`
      );
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getActiveCustomers = createAsyncThunk(
  "Customer/getActive",
  async (payload = {}, { rejectWithValue }) => {
    try {
      const safePayload = payload && typeof payload === "object" ? payload : {};
      const filteredPayload = Object.fromEntries(
        Object.entries(safePayload).filter(
          ([, value]) => value !== null && value !== undefined && value !== ""
        )
      );
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.CUSTOMER.ACTIVE_LOOKUP}?${queryString}`
      );
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * Fetch active customers with FULL details for Sales Order form
 * Uses /BackOffice/Customer/Active endpoint
 * Returns: Id, CompanyName, DisplayName, GSTNumber, BillingAddress, ShippingAddress, State info, CustomerType
 * Supports: SearchText, PageNumber, PageSize (fixed sorting by DisplayName ASC)
 */
export const getActiveCustomersForOrder = createAsyncThunk(
  "Customer/getActiveForOrder",
  async (payload = {}, { rejectWithValue }) => {
    try {
      // Only send supported params: SearchText, PageNumber, PageSize
      // Do NOT send orderColumn or orderBy - API uses fixed sorting
      const params = new URLSearchParams();

      if (payload.SearchText) {
        params.append("SearchText", payload.SearchText);
      }
      if (payload.PageNumber) {
        params.append("PageNumber", payload.PageNumber);
      }
      if (payload.PageSize) {
        params.append("PageSize", payload.PageSize);
      }

      const queryString = params.toString();
      const url = queryString
        ? `${API_URL.CUSTOMER.ACTIVE_LOOKUP}?${queryString}`
        : API_URL.CUSTOMER.ACTIVE_LOOKUP;

      const { data } = await axios.get(url);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createCustomer = createAsyncThunk(
  "Customer/create",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL.CUSTOMER.SLUG}`, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const deleteCustomer = createAsyncThunk(
  "Customer/delete",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete(`${API_URL.CUSTOMER.SLUG}/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const updateCustomerStatus = createAsyncThunk(
  "Customer/updateStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.CUSTOMER.SLUG}/UpdateStatus/${id}/${status}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.data || error);
    }
  }
);

export const getAssignedSalesPersons = createAsyncThunk(
  "Customer/getAssignedSalesPersons",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.CUSTOMER.ASSIGNED_SALES_PERSONS
      );
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const reassignSalesPerson = createAsyncThunk(
  "Customer/reassignSalesPerson",
  async (payload, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.put(
        API_URL.CUSTOMER.REASSIGN_SALES_PERSON,
        payload
      );
      const { active_payload } = getState().customer;
      dispatch(getCustomers(active_payload));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getNextCustomerCode = createAsyncThunk(
  "Customer/getNextCustomerCode",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.CUSTOMER.GET_NEXT_CUSTOMER_CODE);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Advisory duplicate-GST lookup for the Customer form; result is kept in form-local state.
export const checkCustomerGst = createAsyncThunk(
  "Customer/checkGst",
  async ({ gstNumber, excludeId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.CUSTOMER.CHECK_GST, {
        params: { gstNumber, excludeId },
      });
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const editCustomer = createAsyncThunk(
  "Customer/put",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`${API_URL.CUSTOMER.SLUG}`, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const exportCustomersToExcel = createAsyncThunk(
  "Customer/exportExcel",
  async (payload, { rejectWithValue }) => {
    try {
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== ""
        )
      );

      const response = await axios.post(
        API_URL.CUSTOMER.EXPORT_EXCEL,
        filteredPayload,
        {
          responseType: "blob",
          headers: {
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
        }
      );

      // Create download link
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Customers_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const customerSlice = createSlice({
  name: "customer",
  initialState: {
    customer_list: [],
    active_customer_list: [],
    current_values: null,
    active_payload: null,
    total_records: 0,
    next_customer_code: null,
  },
  reducers: {
    setCurrentValues(state, action) {
      state.current_values = action.payload;
    },
    setActivePayload(state, action) {
      state.active_payload = action.payload;
    },
    resetCurrentValue(state) {
      state.current_values = null;
      state.customer_list = [];
    },
    resetNextCustomerCode(state) {
      state.next_customer_code = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCustomers.fulfilled, (state, action) => {
      state.customer_list = action.payload.data.list;
      state.total_records = action.payload.data.totalRecords || 0;
    });
    builder.addCase(getActiveCustomers.fulfilled, (state, action) => {
      state.active_customer_list = action.payload?.data?.list || action.payload?.data || [];
    });
    builder.addCase(getNextCustomerCode.fulfilled, (state, action) => {
      state.next_customer_code = action.payload;
    });
  },
});

export const { setCurrentValues, setActivePayload, resetCurrentValue, resetNextCustomerCode } =
  customerSlice.actions;
export default customerSlice.reducer;
